import { useResource, Resources } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { Typography, Stack } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { ButtonLink } from "Components/Shared/ButtonLink";
import { useAllNotifications } from "Api/Queries/Notifications/useAllNotifications";
import { useDeleteNotificationMutation } from "Api/Mutations/Notifications/useDeleteNotificationMutation";
import { useRestoreNotificationMutation } from "Api/Mutations/Notifications/useRestoreNotificationMutation";
import { useNavigate } from "Navigation/useNavigate";
import { NotificationsGroup } from "Components/Notifications/NotificationsManagement/NotificationsGroup";
import { useGroupNotifications } from "Components/Notifications/NotificationsManagement/Hooks/useGroupNotifications";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";

const RESOURCES = Resources.Pages.Content.NotificationsManagement;

export const NotificationsManagementPageContent = () => {
  const { data: notifications, isLoading, isError } = useAllNotifications();
  const { mutate: deleteNotification } = useDeleteNotificationMutation();
  const { mutate: restoreNotification } = useRestoreNotificationMutation();
  const navigate = useNavigate();
  const { t } = useResource();
  const {
    plannedNotifications,
    dispatchedNotifications,
    deletedNotifications,
  } = useGroupNotifications(notifications || []);

  if (isLoading) {
    return <CenteredCircularProgress />;
  }

  if (isError) {
    return (
      <Typography color="error">{t(Resources.Errors.RequestError)}</Typography>
    );
  }

  const handleEdit = (id: string) => {
    navigate(`${appUrl("edit-notification")}/${id}`);
  };

  return (
    <Stack gap={5}>
      <ButtonLink
        fullWidth
        link={appUrl("new-notification")}
        text={t(RESOURCES.NewNotification)}
        color="primary"
        variant="contained"
        startIcon={<AddIcon />}
      />
      <Stack gap={8}>
        <NotificationsGroup
          title={t(RESOURCES.PlannedNotifications)}
          notifications={plannedNotifications}
          onDelete={deleteNotification}
          onEdit={handleEdit}
        />
        <NotificationsGroup
          title={t(RESOURCES.SentNotifications)}
          notifications={dispatchedNotifications}
          onDelete={deleteNotification}
          onEdit={handleEdit}
        />
        <NotificationsGroup
          isDimmed
          title={t(RESOURCES.DisabledNotifications)}
          notifications={deletedNotifications}
          onRestore={restoreNotification}
        />
      </Stack>
    </Stack>
  );
};
